import { useField } from 'formik';
import { useEffect, useState } from 'react';
import useDebouncedValue from 'hooks/useDebouncedValue';

type UseDebouncedFieldOptions<T> = {
    name: string;
    debounceMs?: number;
    validateOnDebounce?: boolean;
    onDebouncedChange?: (value: T | null) => void;
};

export function useDebouncedField<T>({
    name,
    debounceMs = 250,
    validateOnDebounce,
    onDebouncedChange,
}: UseDebouncedFieldOptions<T>) {
    const [field, meta, helpers] = useField<T | null>(name);

    const [local, setLocal] = useState<T | null>(field.value ?? null);
    const [dirtyFromUser, setDirtyFromUser] = useState(false);

    const debounced = useDebouncedValue(local, debounceMs);

    useEffect(() => {
        setLocal(field.value ?? null);
        setDirtyFromUser(false);
    }, [field.value]);

    // Push debounced value to formik only if the change comes from the user (skips reinitialize from API)
    useEffect(() => {
        if (!dirtyFromUser) return;

        if (debounced !== field.value) {
            helpers.setValue(debounced as T | null, validateOnDebounce);
            onDebouncedChange?.(debounced ?? null);
        }
    }, [debounced, dirtyFromUser, field.value, helpers, onDebouncedChange, validateOnDebounce]);

    const handleChange = (value: T | null) => {
        setDirtyFromUser(true);
        setLocal(value);
    };

    return {
        field,
        meta,
        helpers,
        local,
        setLocal,
        handleChange,
    };
}
